// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/PageHeader";
import PageTitle from "../../../components/PageTitle";
import PageBody from "../../../components/PageBody";
import { navigate, RouteComponentProps } from "@reach/router";
import {
  AuthentifiedUser,
  SurveyInfoResponseDTO
} from "../../../../api/backend-openapi";
import { useStore } from "effector-react";
import { authentifiedUserStore } from "../../../../state/auth/AuthStore";
import { NboAction } from "../../../types/AuthTypes";
import { SpinIcon } from "../../../components/SpinIcon";
import { columns } from "./SurveysListColumns";
import { Button, Divider, message, Table } from "antd";
import { SorterResult, TablePaginationConfig } from "antd/lib/table/interface";
import { PlusCircleOutlined } from "@ant-design/icons";
import { SurveyStore } from "../../../../state/survey/SurveysStore";
import {
  requestDeleteSurvey,
  requestDuplicateSurvey,
  requestGetSurveysList,
  requestWorkflowSurvey
} from "../../../../state/survey/SurveysEffects";
import {
  deleteSurvey,
  setSurveys,
  updateSurvey
} from "../../../../state/survey/SurveysEvents";
import { orderDescend } from "../../../../utils/Constant";
import { BranchesStore } from "../../../../state/branches/BranchesStore";
import { displayError } from "../../../../utils/displayError";
import {
  preferedRowsPerPage,
  preferedRowsPerPageChanged
} from "../../../../state/localPreferences/localPreferences";

const SurveysListScreen: React.FunctionComponent<RouteComponentProps> = () => {
  const { t } = useTranslation();
  const [editMode] = useState<boolean>(false);

  const authentifiedUser: AuthentifiedUser | null = useStore(
    authentifiedUserStore
  );
  const surveyStore: SurveyInfoResponseDTO[] | null = useStore(SurveyStore);
  const canDelete: boolean =
    (authentifiedUser &&
      authentifiedUser.rights &&
      authentifiedUser.rights.nboSurvey &&
      typeof authentifiedUser.rights.nboSurvey.find(
        survey => survey === NboAction.nboDelete
      ) === "string") ||
    false;
  const canCreate: boolean =
    (authentifiedUser &&
      authentifiedUser.rights &&
      authentifiedUser.rights.nboSurvey &&
      typeof authentifiedUser.rights.nboSurvey.find(
        survey => survey === NboAction.nboCreate
      ) === "string") ||
    false;

  const branchList = useStore(BranchesStore);

  const [dataHasLoaded, setDataHasLoaded] = useState<boolean>(true);

  const rowsPerPage = useStore(preferedRowsPerPage);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: rowsPerPage,
    showSizeChanger: true
  });

  useEffect(() => {
    return requestDuplicateSurvey.done.watch(({ result }) => {
      if (result.ok && result.data) {
        navigate(`/surveys/${result.data.id}`);
        message.success(t("status_survey.duplicate.success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  const handleTableChange = async (
    newPagination: any,
    filters: any,
    sorter: SorterResult<any> | SorterResult<any>[]
  ) => {
    const restSorter = Array.isArray(sorter)
      ? sorter.length > 0
        ? sorter[0]
        : undefined
      : sorter;
    const searchCriteria = {
      pagination: newPagination,
      sorter: restSorter,
      dto: filters
    };
    await requestGetSurveysList(searchCriteria);
    setPagination(newPagination);
    preferedRowsPerPageChanged(newPagination.pageSize);
  };

  useEffect(() => {
    requestGetSurveysList({
      pagination,
      sorter: {
        columnKey: "creationDate",
        order: orderDescend
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return requestGetSurveysList.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setSurveys(result.data.listSurveyDTO);
        setDataHasLoaded(true);
        setPagination({
          ...pagination,
          total: result.data.count
        });
        preferedRowsPerPageChanged(pagination.pageSize);
      } else {
        displayError("surveys.load_failed");
      }
    });
  });

  useEffect(() => {
    return requestDeleteSurvey.done.watch(({ params, result }) => {
      if (result.ok) {
        deleteSurvey(params.id);
        message.success(t("survey.global_buttons.delete.success"));
      } else {
        displayError("survey.global_buttons.delete.error");
      }
    });
  });

  useEffect(() => {
    return requestWorkflowSurvey.done.watch(({ result }) => {
      if (result.ok && result.data) {
        updateSurvey(result.data);
        message.success(t("survey.global_buttons.unpublish.success"));
      } else {
        displayError("survey.global_buttons.unpublish.error");
      }
    });
  });

  return (
    <>
      <PageHeader>
        <PageTitle
          title={t(editMode ? "surveys.edit.title" : "surveys.title")}
        />
      </PageHeader>
      <PageBody>
        <div className="d-flex flex-row justify-content-end align-items-stretch flex-wrap">
          <div className="flex-grow-1 d-flex align-items-center">
            <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
              <h3 className="text-secondary mb-0">{t("surveys.list_title")}</h3>
            </Divider>
          </div>
          <div className="d-flex flex-row align-items-center justify-content-end">
            {canCreate && (
              <Button
                type="default"
                className="btn-secondary m-2"
                onClick={() => {
                  navigate("/surveys/add");
                }}
              >
                <PlusCircleOutlined /> {t("surveys.global_buttons.add")}
              </Button>
            )}
          </div>
        </div>
        <Table
          style={{ whiteSpace: "break-spaces" }}
          locale={{ emptyText: dataHasLoaded ? "" : " " }}
          loading={{ spinning: !dataHasLoaded, indicator: SpinIcon }}
          columns={columns(canDelete, branchList)}
          dataSource={surveyStore}
          onChange={handleTableChange}
          pagination={pagination}
          showSorterTooltip={false}
          rowKey={record => record.id}
          rowClassName="cursor-pointer"
          onRow={record => {
            return {
              onClick: () => navigate(`/surveys/${record.id}`)
            };
          }}
        />
      </PageBody>
    </>
  );
};

export default SurveysListScreen;
